// Titulares (Etapa 10a): as "pastas" por advogado responsável na página de credenciais.
// Titular só organiza a tela; quem pode usar uma credencial continua sendo decidido pelos grupos.

export interface Holder {
  id: string;
  name: string;
  oab_number: string | null;
  oab_uf: string | null;
  active: boolean;
  credential_count: number;
}

/** Chave da pasta "Sem titular" (credenciais com holder_id vazio). */
export const SEM_TITULAR = 'sem-titular';

export interface Pasta {
  /** id do titular, ou SEM_TITULAR */
  chave: string;
  nome: string;
  oab: string | null;
  quantidade: number;
}

interface CredencialComTitular {
  holder_id?: string | null;
  holder_name?: string | null;
}

export function formatarOab(holder: Pick<Holder, 'oab_number' | 'oab_uf'>): string | null {
  return holder.oab_number && holder.oab_uf ? `OAB ${holder.oab_number}/${holder.oab_uf}` : null;
}

export function chaveDaPasta(credencial: CredencialComTitular): string {
  return credencial.holder_id || SEM_TITULAR;
}

const ordemAlfabetica = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });

/**
 * Monta as pastas a partir das credenciais que a pessoa vê.
 * - Cada titular de `holders` vira uma pasta, mesmo vazia, se `incluirVazias` (owner/admin organizando).
 * - Titular que aparece numa credencial mas não está em `holders` também vira pasta (pelo holder_name).
 * - Ordem alfabética; "Sem titular" por último e só quando há credenciais sem titular.
 * - A contagem vem das credenciais recebidas (já filtradas por grupo, se houver filtro).
 */
export function agruparPorTitular(
  credenciais: CredencialComTitular[],
  holders: Holder[],
  incluirVazias = true
): Pasta[] {
  const contagem = new Map<string, number>();
  const nomesVistos = new Map<string, string>();
  for (const credencial of credenciais) {
    const chave = chaveDaPasta(credencial);
    contagem.set(chave, (contagem.get(chave) || 0) + 1);
    if (credencial.holder_id && credencial.holder_name) nomesVistos.set(credencial.holder_id, credencial.holder_name);
  }

  const pastas: Pasta[] = [];
  const conhecidos = new Set<string>();
  for (const holder of holders) {
    conhecidos.add(holder.id);
    const quantidade = contagem.get(holder.id) || 0;
    if (quantidade === 0 && !incluirVazias) continue;
    pastas.push({ chave: holder.id, nome: holder.name, oab: formatarOab(holder), quantidade });
  }
  for (const [id, nome] of nomesVistos) {
    if (!conhecidos.has(id)) pastas.push({ chave: id, nome, oab: null, quantidade: contagem.get(id) || 0 });
  }
  pastas.sort((a, b) => ordemAlfabetica(a.nome, b.nome));

  const semTitular = contagem.get(SEM_TITULAR) || 0;
  if (semTitular > 0) pastas.push({ chave: SEM_TITULAR, nome: 'Sem titular', oab: null, quantidade: semTitular });
  return pastas;
}

/** Busca da página de credenciais: mesmos campos de sempre, mais o nome do titular. */
export function credencialCombinaComBusca(
  credencial: CredencialComTitular & { label?: string; system_type?: string; tribunal_slug?: string; url_patterns?: string[] },
  termo: string
): boolean {
  const busca = termo.toLowerCase();
  return (
    (credencial.label || '').toLowerCase().includes(busca) ||
    (credencial.system_type || '').toLowerCase().includes(busca) ||
    (credencial.tribunal_slug || '').toLowerCase().includes(busca) ||
    (credencial.url_patterns || []).some((padrao) => padrao.toLowerCase().includes(busca)) ||
    (credencial.holder_name || '').toLowerCase().includes(busca)
  );
}
